/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Logger, LogService } from '@epg/angular-base-lib';
import { firstValueFrom, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SearchModel } from '../models/search.model';
import { WorkflowVersionModel } from '../models/workflow-version.model';
import { WorkflowCreationModel, WorkflowModel } from '../models/workflow.model';
import {
  UpdateWorkflowNotificationTrigger,
  WorkflowNotificationTrigger
} from '../models/workflowNotificationTrigger.model';
import { ApiService, DesignerApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {
  workflowsChanged = new Subject<WorkflowModel[]>();
  workflowChanged = new Subject<WorkflowModel>();
  workflows: WorkflowModel[] = [];
  private combinedWorkflowsCache: WorkflowModel[] = [];
  private combinedWorkflowsLoaded = false;
  private combinedWorkflowsLoading = false;
  private log: Logger;

  constructor(
    private apiService: ApiService,
    private logService: LogService,
    private designerApiService: DesignerApiService,
    private router: Router
  ) {
    this.log = logService.getLogger(this);
  }

  async searchWorkflow(searchBody: SearchModel): Promise<WorkflowModel[]> {
    try {
      const workflows = await firstValueFrom(this.apiService.search('search/workflow', searchBody));
      // Don't overwrite global workflows or emit workflowsChanged - this is just a search
      return workflows.body ?? [];
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async getWorkflow(id: string): Promise<WorkflowModel> {
    try {
      const workflow = await firstValueFrom(this.apiService.get(`workflow/${id}`));
      this.workflowChanged.next(workflow);
      return workflow;
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async getDesignerWorkflow(id: string): Promise<WorkflowModel> {
    try {
      return await firstValueFrom(this.designerApiService.get(`workflow/${id}`));
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async getWorkflowVersions(
    name: string,
    include_deleted: boolean = false
  ): Promise<WorkflowVersionModel[]> {
    try {
      return await firstValueFrom(
        this.apiService.post('workflow/versions', { name, include_deleted })
      );
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async createNotificationTrigger(
    workflowNotificationTrigger: UpdateWorkflowNotificationTrigger
  ): Promise<WorkflowNotificationTrigger> {
    try {
      return await firstValueFrom(
        this.apiService.post('notificationtrigger', workflowNotificationTrigger)
      );
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async updateNotificationTrigger(
    workflowNotificationTrigger: UpdateWorkflowNotificationTrigger,
    workflowNotificationTriggerId: string
  ): Promise<WorkflowNotificationTrigger> {
    try {
      return await firstValueFrom(
        this.apiService.patch(
          `notificationtrigger/${workflowNotificationTriggerId}`,
          workflowNotificationTrigger
        )
      );
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async deleteNotificationTrigger(
    workflowNotificationTriggerId: string
  ): Promise<WorkflowNotificationTrigger> {
    try {
      return await firstValueFrom(
        this.apiService.delete(`notificationtrigger/${workflowNotificationTriggerId}`)
      );
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async searchNotificationTrigger(workflowName: string): Promise<WorkflowNotificationTrigger[]> {
    const searchBody = {
      search_criteria: { object_reference: workflowName },
      include_deleted: false,
      sort: '',
      select: '',
      skip: 0,
      limit: 0
    };
    try {
      const triggers = await firstValueFrom(
        this.apiService.search('search/notificationtrigger', searchBody)
      );
      return triggers.body || [];
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  async workflowExistsInDesigner(workflowName: string): Promise<WorkflowModel | null> {
    const searchBody = {
      search_criteria: {
        name: { $regex: `^${workflowName}$`, $options: 'i' }
      }
    };
    try {
      const designerWorkflows: WorkflowModel[] = await firstValueFrom(
        this.designerApiService.post('search/workflow', searchBody)
      );
      return (
        designerWorkflows.find(
          (value: WorkflowModel) => value.name.toLowerCase() === workflowName.toLowerCase()
        ) || null
      );
    } catch (error) {
      this.log.error(error);
      throw error;
    }
  }

  getWorkflowNameFromId(id: string): string | undefined {
    return this.workflows.find((wf) => wf._id === id)?.name;
  }

  getCombinedIdFromWorkflowId(id: string): string | undefined {
    this.ensureCombinedWorkflowsLoaded();

    const combined = this.combinedWorkflowsCache.find((wf) => wf._id === id);

    if (!combined) {
      return undefined;
    }

    if (combined.combinedId) {
      return combined.combinedId;
    }

    if (combined.designerId !== undefined) {
      return `${combined._id ?? ''}_${combined.designerId ?? ''}`;
    }

    if (combined._id) {
      return `${combined._id}_`;
    }

    return undefined;
  }

  getWorkflowIdFromName(name: string): string | undefined {
    return this.workflows.find((wf) => wf.name === name)?._id;
  }

  getReferencesFromWorkflow(id: string): string[] {
    return this.workflows.find((wf) => wf._id === id)?.references ?? [];
  }

  async createWorkflowInDesigner(
    workflowCreationModel: WorkflowCreationModel
  ): Promise<WorkflowModel> {
    try {
      const workflow = await firstValueFrom(
        this.designerApiService.post('workflow', workflowCreationModel)
      );
      const createdWorkflow: WorkflowModel = {
        _id: workflow._id || '',
        name: workflow.name || workflowCreationModel.name,
        description: workflow.description || workflowCreationModel.description || '',
        initialState: workflow.initialState,
        states: workflow.states || [],
        createdAt: workflow.createdAt || new Date().toISOString(),
        updatedAt: workflow.updatedAt || new Date().toISOString(),
        is_valid: workflow.is_valid || false,
        is_published: workflow.is_published || false,
        is_draft: workflow.is_draft !== undefined ? workflow.is_draft : true,
        version: workflow.published_version,
        combinedId: `_${workflow._id}`,
        input: {},
        output: {},
        company_id: workflow.company_id
      };

      // Refresh combined workflows so the list reflects the newly created workflow
      try {
        const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
        this.workflowsChanged.next(combinedWorkflows);
      } catch (refreshError) {
        this.log.error('Error refreshing combined workflows after creation:', refreshError);
      }

      return createdWorkflow;
    } catch (error) {
      this.log.error('Error creating workflow in designer:', error);
      throw error;
    }
  }

  async getAllWorkflowsForValidation(): Promise<WorkflowModel[]> {
    const workflowSearchParams = {
      search_criteria: { is_deleted: { $not: { $eq: true } } },
      limit: 0,
      skip: 0,
      sort: '-updatedAt',
      select: '_id name'
    };

    const designerSearchParams = {
      search_criteria: {},
      skip: 0,
      limit: 0,
      sort: 'name',
      select: '_id name'
    };

    try {
      const [workflows, designerWorkflows] = await Promise.all([
        firstValueFrom(this.apiService.search('search/workflow', workflowSearchParams)),
        firstValueFrom(this.designerApiService.post('search/workflow', designerSearchParams))
      ]);

      const allWorkflows = [...((workflows?.body ?? []) as WorkflowModel[])];
      const existingNames = new Set(allWorkflows.map((w) => w.name.toLowerCase()));

      (designerWorkflows as any[]).forEach((designerWorkflow) => {
        if (!existingNames.has(designerWorkflow.name.toLowerCase())) {
          allWorkflows.push({
            _id: designerWorkflow._id,
            name: designerWorkflow.name
          } as WorkflowModel);
        }
      });

      return allWorkflows;
    } catch (error) {
      this.log.error('Error fetching workflows for validation:', error);
      try {
        return ((
          await firstValueFrom(this.apiService.search('search/workflow', workflowSearchParams))
        ).body ?? []) as WorkflowModel[];
      } catch (fallbackError) {
        this.log.error('Error fetching OneApp workflows as fallback:', fallbackError);
        return [];
      }
    }
  }

  getCombinedWorkflowById(id: string): Observable<WorkflowModel> {
    const { publishedId, designerId } = this.parseCombinedId(id);

    if (!publishedId && !designerId) {
      return throwError(() => new Error(`Invalid combined workflow ID: ${id}`));
    }

    const published$ = publishedId
      ? this.apiService.get(`workflow/${publishedId}`).pipe(
          catchError((err) => {
            this.log.debug('Published workflow not found:', publishedId);
            return of(null);
          })
        )
      : of(null);

    const designer$ = designerId
      ? this.designerApiService.get(`workflow/${designerId}`).pipe(
          catchError((err) => {
            this.log.debug('Designer workflow not found:', designerId);
            return of(null);
          })
        )
      : of(null);

    return forkJoin([published$, designer$]).pipe(
      map(([publishedWorkflow, designerWorkflow]) => {
        if (!publishedWorkflow && !designerWorkflow) {
          throw new Error(`Workflow with ID ${id} not found.`);
        }

        return this.combineWorkflow(publishedWorkflow, designerWorkflow, id);
      }),
      catchError((err) => {
        this.log.error('Error fetching combined workflow by ID:', err);
        return throwError(
          () => new Error(`Workflow with ID ${id} not found.`)
        ) as Observable<WorkflowModel>;
      })
    );
  }
  private parseCombinedId(combinedId: string): { publishedId: string; designerId: string } {
    const parts = combinedId.split('_');
    return {
      publishedId: parts[0] || '',
      designerId: parts[1] || ''
    };
  }

  private combineWorkflow(
    publishedWorkflow: any | null,
    designerWorkflow: any | null,
    combinedId: string
  ): WorkflowModel {
    if (publishedWorkflow && designerWorkflow) {
      return {
        _id: publishedWorkflow._id,
        company_id: publishedWorkflow.company_id,
        name: publishedWorkflow.name,
        description: designerWorkflow.description ?? publishedWorkflow.description,
        initialState: publishedWorkflow.initialState,
        input: publishedWorkflow.input,
        output: publishedWorkflow.output,
        states: publishedWorkflow.states,
        createdAt: publishedWorkflow.createdAt,
        updatedAt: publishedWorkflow.updatedAt,
        version: publishedWorkflow.version,
        executableFromMobile: publishedWorkflow.executableFromMobile,
        locationTracingEnabled:
          designerWorkflow.locationTracingEnabled ?? publishedWorkflow.locationTracingEnabled,
        enableStatesTimestamps:
          designerWorkflow.enableStatesTimestamps ?? publishedWorkflow.enableStatesTimestamps,
        designerId: designerWorkflow._id,
        is_published: designerWorkflow.is_published ?? true,
        is_draft: designerWorkflow.is_draft,
        is_valid: designerWorkflow.is_valid,
        availableInDesigner: true,
        combinedId: combinedId
      } as WorkflowModel;
    } else if (publishedWorkflow) {
      return {
        _id: publishedWorkflow._id,
        company_id: publishedWorkflow.company_id,
        name: publishedWorkflow.name,
        description: publishedWorkflow.description,
        initialState: publishedWorkflow.initialState,
        input: publishedWorkflow.input,
        output: publishedWorkflow.output,
        states: publishedWorkflow.states,
        createdAt: publishedWorkflow.createdAt,
        updatedAt: publishedWorkflow.updatedAt,
        version: publishedWorkflow.version,
        executableFromMobile: publishedWorkflow.executableFromMobile,
        locationTracingEnabled: publishedWorkflow.locationTracingEnabled,
        enableStatesTimestamps: publishedWorkflow.enableStatesTimestamps,
        is_published: true,
        availableInDesigner: false,
        combinedId: combinedId
      } as WorkflowModel;
    } else {
      return {
        _id: '',
        company_id: designerWorkflow.company_id,
        name: designerWorkflow.name,
        description: designerWorkflow.description,
        initialState: designerWorkflow.initialState,
        input: {},
        output: {},
        states: designerWorkflow.states,
        createdAt: designerWorkflow.createdAt,
        updatedAt: designerWorkflow.updatedAt,
        version: 0,
        executableFromMobile: false,
        locationTracingEnabled: designerWorkflow.locationTracingEnabled,
        enableStatesTimestamps: designerWorkflow.enableStatesTimestamps,
        designerId: designerWorkflow._id,
        is_published: designerWorkflow.is_published,
        is_draft: !designerWorkflow.is_published,
        is_valid: designerWorkflow.is_valid,
        availableInDesigner: true,
        combinedId: combinedId
      } as WorkflowModel;
    }
  }

  getCombinedWorkflows(): Observable<WorkflowModel[]> {
    const workflowSearchParams = {
      search_criteria: { is_deleted: { $ne: true } },
      skip: 0,
      limit: 0,
      sort: 'name',
      select:
        '_id name company_id version createdAt updatedAt executableFromMobile description initialState locationTracingEnabled enableStatesTimestamps'
    };

    const designerSearchParams = {
      search_criteria: {},
      skip: 0,
      limit: 0,
      sort: 'name',
      select:
        '_id name company_id is_published is_draft description createdAt updatedAt initialState is_valid locationTracingEnabled enableStatesTimestamps'
    };

    const workflows$ = this.apiService.search('search/workflow', workflowSearchParams).pipe(
      map((response) => {
        return response.body ?? [];
      }),
      catchError((err) => {
        this.log.error('Error fetching workflows:', err);
        return of([]);
      })
    );

    const designerWorkflows$ = this.designerApiService
      .post('search/workflow', designerSearchParams)
      .pipe(
        catchError((err) => {
          this.log.error('Error fetching designer workflows:', err);
          return of([]);
        })
      );

    return forkJoin([workflows$, designerWorkflows$]).pipe(
      map(([workflows, designerWorkflows]) => {
        const designerWorkflowMap = new Map<string, any>();

        (designerWorkflows as WorkflowModel[]).forEach((workflow: WorkflowModel) => {
          designerWorkflowMap.set(workflow.name.toLowerCase(), {
            _id: workflow._id,
            is_published: workflow.is_published,
            is_draft: workflow.is_draft,
            is_valid: workflow.is_valid,
            description: workflow.description,
            locationTracingEnabled: workflow.locationTracingEnabled,
            enableStatesTimestamps: workflow.enableStatesTimestamps
          });
        });

        const combinedWorkflows: WorkflowModel[] = (workflows as any[]).map((workflow) => {
          const designerWorkflow = designerWorkflowMap.get(workflow.name.toLowerCase());

          return {
            _id: workflow._id,
            company_id: workflow.company_id,
            name: workflow.name,
            description: designerWorkflow?.description ?? workflow.description,
            initialState: workflow.initialState,
            input: workflow.input,
            output: workflow.output,
            states: workflow.states,
            createdAt: workflow.createdAt,
            updatedAt: workflow.updatedAt,
            version: workflow.version,
            executableFromMobile: workflow.executableFromMobile,
            locationTracingEnabled:
              designerWorkflow?.locationTracingEnabled ?? workflow.locationTracingEnabled,
            enableStatesTimestamps:
              designerWorkflow?.enableStatesTimestamps ?? workflow.enableStatesTimestamps,
            designerId: designerWorkflow?._id,
            is_published: designerWorkflow?.is_published ?? true,
            is_draft: designerWorkflow?.is_draft,
            is_valid: designerWorkflow?.is_valid,
            availableInDesigner: !!designerWorkflow,
            combinedId: `${workflow._id ?? ''}_${designerWorkflow?._id ?? ''}`
          } as WorkflowModel;
        });

        (designerWorkflows as WorkflowModel[]).forEach((designerWorkflow: WorkflowModel) => {
          if (!combinedWorkflows.some((wf) => wf.designerId === designerWorkflow._id)) {
            combinedWorkflows.push({
              _id: '',
              company_id: designerWorkflow.company_id,
              name: designerWorkflow.name,
              description: designerWorkflow.description,
              initialState: designerWorkflow.initialState,
              input: {},
              output: {},
              states: designerWorkflow.states,
              createdAt: designerWorkflow.createdAt,
              updatedAt: designerWorkflow.updatedAt,
              version: 0,
              executableFromMobile: false,
              locationTracingEnabled: designerWorkflow.locationTracingEnabled,
              enableStatesTimestamps: designerWorkflow.enableStatesTimestamps,
              designerId: designerWorkflow._id,
              is_published: designerWorkflow.is_published,
              is_draft: !designerWorkflow.is_published,
              is_valid: designerWorkflow?.is_valid,
              availableInDesigner: true,
              combinedId: `_${designerWorkflow?._id ?? ''}`
            } as WorkflowModel);
          }
        });
        this.workflows = [...combinedWorkflows];
        this.combinedWorkflowsCache = [...combinedWorkflows];
        this.combinedWorkflowsLoaded = true;
        this.combinedWorkflowsLoading = false;
        return combinedWorkflows;
      })
    );
  }

  private ensureCombinedWorkflowsLoaded(): void {
    if (this.combinedWorkflowsLoaded || this.combinedWorkflowsLoading) {
      return;
    }

    this.combinedWorkflowsLoading = true;
    firstValueFrom(this.getCombinedWorkflows())
      .then((combined) => {
        this.combinedWorkflowsCache = combined;
        this.combinedWorkflowsLoaded = true;
      })
      .catch((error) => {
        this.log.error('Error preloading combined workflows:', error);
      })
      .finally(() => {
        this.combinedWorkflowsLoading = false;
      });
  }

  /**
   * Get workflow statistics/analytics data
   * @param workflowId - The workflow ID to get statistics for
   * @param requestBody - The analytics request body with date range and metrics
   * @returns Promise with analytics response
   */
  async getWorkflowStatistics(workflowId: string, requestBody: any): Promise<any> {
    try {
      this.log.debug('Requesting workflow statistics for ID:', workflowId);
      this.log.debug('Request body:', requestBody);

      const statistics = await this.apiService
        .post(`statistics/workflow/${workflowId}`, requestBody)
        .toPromise();

      this.log.debug('Workflow statistics received:', statistics);
      this.log.info('Analytics endpoint response data:', JSON.stringify(statistics, null, 2));

      return statistics;
    } catch (error) {
      this.log.error('Error fetching workflow statistics:', error);
      throw error;
    }
  }

  async navigateToDesigner(combinedId: string) {
    if (combinedId) {
      this.router.navigate(['home', 'workflow', combinedId, 'designer']);
    }
  }

  async duplicateWorkflow(id: string, name: string, description: string): Promise<WorkflowModel> {
    try {
      const result: any = await firstValueFrom(
        this.designerApiService.post(`workflow/${id}/duplicate`, { name, description })
      );

      const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
      this.workflowsChanged.next(combinedWorkflows);

      const workflow = combinedWorkflows.find((workflow) => workflow.designerId === result._id);
      if (workflow) {
        return workflow;
      } else {
        throw new Error('Workflow not found after duplicating.');
      }
    } catch (error) {
      this.log.error('Error duplicating workflow in designer:', error);
      throw error;
    }
  }

  async publishWorkflow(id: string): Promise<WorkflowModel> {
    try {
      await firstValueFrom(this.designerApiService.post(`workflow/${id}/publish`, {}));

      const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
      this.workflowsChanged.next(combinedWorkflows);

      const workflow = combinedWorkflows.find((workflow) => workflow.designerId === id);
      if (workflow) {
        return workflow;
      } else {
        throw new Error('Workflow not found after publishing.');
      }
    } catch (error) {
      this.log.error('Error publishing workflow:', error);
      throw error;
    }
  }

  async unPublishWorkflow(id: string): Promise<WorkflowModel> {
    try {
      await firstValueFrom(this.designerApiService.post(`workflow/${id}/unpublish`, {}));
      const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
      this.workflowsChanged.next(combinedWorkflows);

      const workflow = combinedWorkflows.find((workflow) => workflow.designerId === id);
      if (workflow) {
        return workflow;
      } else {
        throw new Error('Workflow not found after unpublishing.');
      }
    } catch (error) {
      this.log.error('Error unpublishing workflow:', error);
      throw error;
    }
  }

  async deleteWorkflow(id: string, unpublish: boolean = false, rename: boolean = false) {
    try {
      if (unpublish) {
        await this.unPublishWorkflow(id);
      }

      await firstValueFrom(this.designerApiService.delete(`workflow/${id}?rename=${rename}`));
      const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
      this.workflowsChanged.next(combinedWorkflows);
    } catch (error) {
      this.log.error('Error deleting workflow:', error);
      throw error;
    }
  }

  async checkWFNameIsValid(name: string): Promise<boolean> {
    try {
      const isValid = await firstValueFrom(
        this.designerApiService.get(`workflow/${name}/validatename`)
      );
      if (isValid) return true;
      else return false;
    } catch (error) {
      this.log.error('Error validating workflow name:', error);
      throw error;
    }
  }

  private async reloadCombinedWorkflows(): Promise<WorkflowModel[]> {
    const combinedWorkflows = await firstValueFrom(this.getCombinedWorkflows());
    this.workflowsChanged.next(combinedWorkflows);
    return combinedWorkflows;
  }

  private async reloadWorkflowByDesignerId(
    designerId: string,
    notFoundMessage: string
  ): Promise<WorkflowModel> {
    const combinedWorkflows = await this.reloadCombinedWorkflows();
    const workflow = combinedWorkflows.find((wf) => wf.designerId === designerId);
    if (!workflow) {
      throw new Error(notFoundMessage);
    }
    return workflow;
  }
}
